// Auto-Pause Rules Performance Dashboard (P2 Enhancement)
import React, { useState, useEffect } from 'react';
import { autoPauseApi } from '../lib/autoPauseApi';

interface PerformanceStats {
  total_rules: number;
  active_rules: number;
  rules_fired_this_month: number;
  budget_saved_estimate: number;
  most_triggered_rule: string | null;
  false_positive_rate: number;
  avg_response_time_hours: number;
}

export const AutoPausePerformanceDashboard: React.FC = () => {
  const [stats, setStats] = useState<PerformanceStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/auto-pause-rules/stats', {
        headers: {
          'Content-Type': 'application/json',
          // Add auth header if needed
          // 'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to load performance stats:', err);
      // Fall back to mock data if API fails
      setStats({
        total_rules: 0,
        active_rules: 0,
        rules_fired_this_month: 0,
        budget_saved_estimate: 0,
        most_triggered_rule: null,
        false_positive_rate: 0,
        avg_response_time_hours: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return null; // Don't show anything while loading
  }

  return (
    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Rules Fired This Month */}
      <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-bodydark dark:text-bodydark1">
            Rules Fired
          </h3>
          <div className="w-10 h-10 rounded-full bg-primary bg-opacity-10 flex items-center justify-center">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-black dark:text-white">
            {stats.rules_fired_this_month}
          </span>
          <span className="text-sm text-bodydark dark:text-bodydark1">
            this month
          </span>
        </div>
      </div>

      {/* Budget Saved */}
      <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-bodydark dark:text-bodydark1">
            Budget Saved
          </h3>
          <div className="w-10 h-10 rounded-full bg-success bg-opacity-10 flex items-center justify-center">
            <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-success">
            ${stats.budget_saved_estimate.toLocaleString()}
          </span>
          <span className="text-sm text-bodydark dark:text-bodydark1">
            estimated
          </span>
        </div>
      </div>

      {/* Active Rules */}
      <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-bodydark dark:text-bodydark1">
            Active Rules
          </h3>
          <div className="w-10 h-10 rounded-full bg-meta-3 bg-opacity-10 flex items-center justify-center">
            <svg className="w-5 h-5 text-meta-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-black dark:text-white">
            {stats.active_rules}
          </span>
          <span className="text-sm text-bodydark dark:text-bodydark1">
            / {stats.total_rules} total
          </span>
        </div>
      </div>

      {/* Most Triggered Rule */}
      <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-bodydark dark:text-bodydark1">
            Top Rule
          </h3>
          <div className="w-10 h-10 rounded-full bg-warning bg-opacity-10 flex items-center justify-center">
            <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </div>
        </div>
        <div className="text-lg font-semibold text-black dark:text-white truncate">
          {stats.most_triggered_rule || 'N/A'}
        </div>
        <div className="text-xs text-bodydark dark:text-bodydark1">
          False positive: {stats.false_positive_rate}%
        </div>
      </div>
    </div>
  );
};
